import { useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "../components/AppShell";
import { Funnel, TrendChart } from "../components/charts";
import {
  Badge,
  Banner,
  Card,
  PanelState,
  RangeFilter,
  StatTile,
} from "../components/ui";
import { compact, decimal, num, pct, usd } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { OverviewResponse } from "../lib/types";

export function OverviewPage() {
  const [days, setDays] = useState(30);
  const { data, error, loading, stale } = useApi<OverviewResponse>(
    "/api/admin/analytics/overview",
    { days },
  );

  const k = data?.kpis;
  const masks = data?.masks;
  const ai = data?.ai;

  return (
    <Page
      title="Overview"
      subtitle={
        data
          ? `Last ${days} days · all times ${data.range.timezone}`
          : "Loading…"
      }
      actions={<RangeFilter days={days} onChange={setDays} />}
    >
      <div className={stale ? "stale" : undefined}>
        <PanelState loading={loading} error={error}>
          {data && k && masks && ai && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {/* A failing mask is a customer looking at a photo they cannot
                  paint, so it is called out above the fold rather than left
                  in a panel further down. 10% is the line at which this stops
                  being noise and starts being an outage. */}
              {masks.failureRate >= 10 && masks.total >= 5 && (
                <Banner tone="critical" title="Surface detection is failing">
                  {pct(masks.failureRate)} of rooms started in this period never
                  got a usable mask ({num(masks.FAILED)} of {num(masks.total)}).
                  Those customers cannot paint anything. Check the mask
                  provider's key and quota.
                </Banner>
              )}

              <div className="grid grid-kpi">
                <StatTile
                  label="New signups"
                  value={k.newUsers.value}
                  kpi={k.newUsers}
                  hint={`${num(k.totalUsers)} total`}
                />
                <StatTile
                  label="Active customers"
                  value={k.activeUsers.value}
                  kpi={k.activeUsers}
                  hint="signed in, edited or rendered"
                />
                <StatTile
                  label="Views generated"
                  value={k.newRenders.value}
                  kpi={k.newRenders}
                  hint={`${decimal(k.rendersPerActiveUser)} per active customer`}
                />
                <StatTile
                  label="Views downloaded"
                  value={k.downloads.value}
                  kpi={k.downloads}
                  hint="the closest thing to intent"
                />
                <StatTile
                  label="Rooms started"
                  value={k.newProjects.value}
                  kpi={k.newProjects}
                  hint={`${num(k.totalProjects)} total`}
                />
                <StatTile
                  label="Live sessions"
                  value={k.activeSessions}
                  hint={`${num(data.sessions.expiringToday)} expire today`}
                />
                <StatTile
                  label="AI spend (est.)"
                  value={usd(ai.cost.value)}
                  kpi={ai.cost}
                  neutral
                  hint={
                    ai.perRenderUsd !== null
                      ? `${usd(ai.perRenderUsd)} per view`
                      : `${num(ai.calls)} model calls`
                  }
                />
                <StatTile
                  label="AI tokens used"
                  value={compact(ai.totalTokens)}
                  hint={`${num(ai.calls)} model call${ai.calls === 1 ? "" : "s"}`}
                />
              </div>

              <Card
                title="Activity"
                note="Four measures of the same days. One axis — a second scale would invent a relationship the data does not contain."
              >
                <TrendChart
                  data={data.series}
                  series={[
                    { key: "signups", label: "Signups" },
                    { key: "renders", label: "Views generated" },
                    { key: "projects", label: "Rooms started" },
                    { key: "activeUsers", label: "Active customers" },
                  ]}
                />
              </Card>

              <div className="grid grid-2">
                <Card
                  title="Signup funnel"
                  note={`Of the ${num(data.funnel[0]?.users ?? 0)} people who signed up in this window, how far each got. Not a count of activity — a cohort.`}
                >
                  <PanelState
                    loading={false}
                    error={null}
                    empty={(data.funnel[0]?.users ?? 0) === 0}
                    emptyText="Nobody signed up in this period."
                  >
                    <Funnel stages={data.funnel} />
                  </PanelState>
                </Card>

                <div
                  style={{ display: "flex", flexDirection: "column", gap: 14 }}
                >
                  <Card
                    title="Product health"
                    note="Surface detection is the one step that can fail on its own."
                  >
                    <div className="table-wrap">
                      <table>
                        <tbody>
                          <tr>
                            <td>Rooms with a usable mask</td>
                            <td className="num cell-strong">
                              {num(masks.READY)}
                            </td>
                          </tr>
                          <tr>
                            <td>Still generating</td>
                            <td className="num">{num(masks.PENDING)}</td>
                          </tr>
                          <tr>
                            <td>Failed</td>
                            <td className="num">
                              <span
                                style={{
                                  display: "inline-flex",
                                  gap: 8,
                                  alignItems: "center",
                                }}
                              >
                                {num(masks.FAILED)}
                                <Badge
                                  tone={
                                    masks.failureRate >= 10
                                      ? "critical"
                                      : masks.failureRate >= 3
                                        ? "warning"
                                        : "good"
                                  }
                                >
                                  {pct(masks.failureRate)}
                                </Badge>
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Card>

                  <Card
                    title="AI spend"
                    note="Estimated from the tokens each image-model call reported. The invoice is the authority; this is what lets you check it."
                    aside={
                      <Link to="/usage" className="btn btn-sm">
                        Every call
                      </Link>
                    }
                  >
                    <div className="table-wrap">
                      <table>
                        <tbody>
                          <tr>
                            <td>Estimated cost</td>
                            <td className="num cell-strong">
                              {usd(ai.cost.value)}
                            </td>
                          </tr>
                          <tr>
                            <td>Average per view generated</td>
                            <td className="num">{usd(ai.perRenderUsd)}</td>
                          </tr>
                          <tr>
                            <td>Average per room (surface mask)</td>
                            <td className="num">{usd(ai.perMaskUsd)}</td>
                          </tr>
                          <tr>
                            <td>Tokens — all / output image</td>
                            <td className="num">
                              {num(ai.totalTokens)} /{" "}
                              {num(ai.outputImageTokens)}
                            </td>
                          </tr>
                          <tr>
                            <td>Billed, nothing usable</td>
                            <td className="num">
                              <span
                                style={{
                                  display: "inline-flex",
                                  gap: 8,
                                  alignItems: "center",
                                }}
                              >
                                {usd(ai.failedCostUsd)}
                                <Badge
                                  tone={ai.failedCalls > 0 ? "warning" : "neutral"}
                                >
                                  {num(ai.failedCalls)} call
                                  {ai.failedCalls === 1 ? "" : "s"}
                                </Badge>
                              </span>
                            </td>
                          </tr>
                          {ai.unpricedCalls > 0 && (
                            <tr>
                              <td>Calls with no known price</td>
                              <td className="num">
                                <Badge tone="warning">
                                  {num(ai.unpricedCalls)} not in total
                                </Badge>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Card>

                  <Card
                    title="Coming back"
                    note="Of this window's signups, who was active again more than a day later."
                  >
                    <div className="table-wrap">
                      <table>
                        <tbody>
                          <tr>
                            <td>Returned after day one</td>
                            <td className="num cell-strong">
                              {num(data.retention.returned)} /{" "}
                              {num(data.retention.cohort)}
                            </td>
                          </tr>
                          <tr>
                            <td>Return rate</td>
                            <td className="num cell-strong">
                              {pct(data.retention.rate)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Card>

                  <Card
                    title="Signup drop-off"
                    /* The window is fixed at 24h and says so: OTP rows are
                       purged a day after they expire, so a 90-day figure would
                       divide by a denominator that has mostly been deleted. */
                    note={`Numbers that asked for a code and never became an account. Last ${data.dropOff.windowHours} hours only — OTP records are purged after that, so a longer window would report a rate that isn't real.`}
                  >
                    <div className="table-wrap">
                      <table>
                        <tbody>
                          <tr>
                            <td>Codes requested</td>
                            <td className="num">
                              {num(data.dropOff.requested)}
                            </td>
                          </tr>
                          <tr>
                            <td>Never completed signup</td>
                            <td className="num cell-strong">
                              {num(data.dropOff.abandoned)}{" "}
                              <Badge
                                tone={
                                  data.dropOff.rate >= 50
                                    ? "warning"
                                    : "neutral"
                                }
                              >
                                {pct(data.dropOff.rate, 0)}
                              </Badge>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </PanelState>
      </div>
    </Page>
  );
}
